import { BreakpointObserver } from '@angular/cdk/layout';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Observable, of, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay, switchMap, take, takeUntil } from 'rxjs/operators';
import { Patient } from 'src/app/models/patient.model';
import { NewAuthService } from 'src/app/services/auth/new-auth-service.service';
import { NotificationsCanaryService } from 'src/app/services/notifications-canary.service';
import { NotificationsService } from 'src/app/services/notifications.service';
import { DataService } from 'src/app/services/data.service';
import { InitialTourService } from 'src/app/services/initial-tour.service';
import { InitialDataModel } from 'src/app/services/models/initial-data-model.service';
import { PatientListNavigationService } from 'src/app/services/patient-list-navigation.service';
import { RoleModel } from 'src/app/services/models/role-model.service';
import { ThemeService } from 'src/app/services/theme.service';

import { User } from 'src/app/models/websocket.model';
import { PatientService } from 'src/app/services/patient.service';
import { ProfileConfigurationService } from 'src/app/services/profile-configuration.service';
import { SnackService } from 'src/app/services/snack.service';
import { SocketService } from 'src/app/services/websocket.service';
import version from '../../../assets/version.json';
import { BugReportDialogComponent } from '../dialogs/bug-report-dialog/bug-report-dialog.component';

@Component({
  selector: 'app-shell',
  templateUrl: './shell.component.html',
  styleUrls: ['./shell.component.scss'],
})
export class ShellComponent implements OnInit, OnDestroy {
  versionStr = `v ${version.major}.${version.minor}.${version.patch}`;
  isLightMode$: Observable<boolean>;
  isAuthView = false;
  isPatientDetailView = false;
  loading = false;
  themeToggleControl = new UntypedFormControl(false);

  /** The bell owns whether the panel is showing, so the two cannot disagree about it. */
  notificationsOpen = false;

  /** The bell, so focus can be returned to it when the panel closes. Behind an `*ngIf`, hence not static. */
  @ViewChild('notificationsBell', { static: false }) notificationsBell?: ElementRef<HTMLButtonElement>;

  /** Whether this user is in the notifications canary group. Gates the bell and the panel. */
  readonly notificationsEnabled$ = this.notificationsCanary.isEnabled$;

  /**
   * Drives the badge. Subscribed by the toolbar whether or not the panel is open, because an unread
   * count that only appears after you open the panel tells you nothing you did not already know.
   *
   * Behind the canary via `switchMap`, not merely hidden by the template's `*ngIf`: a user outside
   * the group must not open the live Firestore listener at all. Hiding the bell while still
   * subscribing would keep paying for reads nobody can see, and would leave a listener running for
   * every user in the org — which is the opposite of what a canary is for.
   */
  readonly notificationsUnread$ = this.notificationsEnabled$.pipe(
    switchMap((enabled) => (enabled ? this.notificationsService.myNotifications$() : of([]))),
    map((rows) => rows.filter((row) => !row.read).length),
    // The template binds this once, so this is insurance rather than the fix — the fix is the
    // `shareReplay` inside `myNotifications$()`, which is what stops a second consumer opening a
    // second Firestore listener. This keeps a future second binding here from re-running the filter.
    shareReplay({ bufferSize: 1, refCount: true })
  );

  toggleNotifications(): void {
    this.notificationsOpen = !this.notificationsOpen;
  }

  /**
   * Closes the panel and puts focus back on the bell.
   *
   * The panel moves focus INTO itself when it opens; returning it is the shell's job because the
   * shell owns the bell. Without this, closing with Escape or the X drops focus onto `<body>` and a
   * keyboard user restarts their tab journey from the top of the page.
   */
  closeNotifications(): void {
    this.notificationsOpen = false;
    this.notificationsBell?.nativeElement.focus();
  }
  showTour$: Observable<boolean>;

  isHandset$: Observable<boolean> = this.breakpointObserver.observe(['(max-width: 1283px)']).pipe(
    map((result) => result.matches),
    shareReplay()
  );
  // Lives as long as the component. Never reassign it: every takeUntil(this.unsubscribe) captures
  // the instance that existed when the chain was built, so swapping it orphans all of them.
  unsubscribe: Subject<void> = new Subject<void>();
  users: { id: string; name: string }[] = [];

  // Scoped to one patient-detail visit. Emits on every route change so the previous chart's socket
  // subscriptions die, and is reused across visits — so it is completed only in ngOnDestroy.
  private leftPatientDetail = new Subject<void>();

  constructor(
    private breakpointObserver: BreakpointObserver,
    public data: DataService,
    public auth: NewAuthService,
    public roleModel: RoleModel,
    private themeService: ThemeService,
    private matDialog: MatDialog,
    private router: Router,
    private route: ActivatedRoute,
    private initialDataService: InitialDataModel,
    private initialTourService: InitialTourService,
    private profileConfigurationService: ProfileConfigurationService,
    public patientService: PatientService,
    private socketService: SocketService,
    private snackBar: SnackService,
    private patientListNavService: PatientListNavigationService,
    private notificationsService: NotificationsService,
    private notificationsCanary: NotificationsCanaryService
  ) {}

  ngOnInit() {
    this.showTour$ = this.initialDataService.isTourShownObservable$;
    this.isLightMode$ = this.themeService.isLightModeObservable$;

    // Track route changes to detect auth views
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.unsubscribe)
      )
      .subscribe((event: NavigationEnd) => {
        this.isAuthView =
          event.url.includes('/auth/') ||
          event.url === '/sign-in' ||
          event.url === '/sign-up' ||
          event.url === '/forgot-password';
        this.isPatientDetailView = event.url.includes('patient-detail');
      });

    this.themeToggleControl.valueChanges
      .pipe(takeUntil(this.unsubscribe), distinctUntilChanged())
      .subscribe((isLight) => {
        const theme = isLight ? 'light' : 'dark';
        this.themeService.toggleDarkMode(theme);
        const userId = this.auth.user?.user_id;
        if (userId) {
          this.profileConfigurationService
            .updateProfileConfiguration(userId, 'theme', { default_theme: theme })
            .catch((err) => console.error('Failed to persist theme preference', err));
        }
      });

    this.showTour$
      .pipe(
        takeUntil(this.unsubscribe),
        distinctUntilChanged(),
        filter((showTour) => showTour === true)
      )
      .subscribe(() => this.startTour());
    this.auth.isLoggedObservable$
      .pipe(takeUntil(this.unsubscribe), filter((isLogged) => !!isLogged), take(1))
      .subscribe(() => {
        this.getProfileTheme();
      });

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(() => {
        this.checkIfSpecificRoute();
      });
  }

  joinPatientDetail(): void {
    const patientId = this.router.url.split('/')[3];
    const userName = `${this.auth.user?.firstName} ${this.auth.user?.lastName}`;
    this.socketService.joinPatientDetail(this.auth.user?.user_id, userName, patientId);
    this.socketService
      .onUserJoined()
      .pipe(takeUntil(this.leftPatientDetail))
      .subscribe((msg) => this.showPopup(msg));
    this.socketService
      .onInitialUsersMessage()
      .pipe(takeUntil(this.leftPatientDetail))
      .subscribe((msg) => this.showPopup(msg));
    this.socketService
      .onUsers()
      .pipe(takeUntil(this.leftPatientDetail))
      .subscribe((users: User[]) => {
        const userIds = users.map((user) => user.id);
        const uniqueUserIds = [...new Set(userIds)];
        this.users = uniqueUserIds.map((userId) => {
          const user = users.find((u) => u.id === userId);
          return {
            id: user.id,
            name: user.name,
            tooltip: user.id !== this.auth.user?.user_id ? `${user.name} connected` : 'You',
            initials:
              user.name?.split(' ').length > 1
                ? `${user.name[0].toUpperCase()}${user.name.split(' ')[1][0].toUpperCase()}`
                : `${user.name[0].toUpperCase()}`,
          };
        });
      });
  }

  showPopup(msg: string): void {
    this.snackBar.genericSnackBar(msg, ['success-snackbar'], 6000);
  }

  checkIfSpecificRoute() {
    // Drop the previous chart's socket subscriptions on every navigation, including chart A -> B.
    this.leftPatientDetail.next();
    if (this.router.url.includes('clinical/patient-detail')) {
      this.joinPatientDetail();
    } else {
      this.users = [];
      this.socketService.leavePatientDetail();
    }
  }

  getProfileTheme() {
    this.profileConfigurationService
      .getProfileConfiguration(this.auth.user?.user_id, 'theme')
      .pipe(take(1), takeUntil(this.unsubscribe))
      .subscribe((profileConfiguration) => {
        const isLight = profileConfiguration?.default_theme === 'light';
        this.themeToggleControl.setValue(isLight, { emitEvent: false });
        this.themeService.toggleDarkMode(profileConfiguration?.default_theme ?? 'dark');
      });
  }

  ngOnDestroy(): void {
    this.leftPatientDetail.next();
    this.leftPatientDetail.complete();
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  openBugReportForm(): void {
    const dialogRef = this.matDialog.open(BugReportDialogComponent, {
      width: '700px',
      maxWidth: '100vw',
    });
  }

  navigateToPatientDetail(patient: Patient): void {
    this.router.navigateByUrl(`/clinical/patient-detail/${patient.user_id}/home`).then((navigated) => {
      if (navigated) {
        this.patientListNavService.clearChartNavigationContext();
      }
    });
  }

  navigateToIntegrationPage(): void {
    this.router.navigateByUrl(`/integration`);
  }

  logout() {
    this.auth.signOut();
    this.initialDataService.clearSubscriptions();
    this.initialDataService.resetValues();
  }

  startTour() {
    this.initialTourService.startTour();
  }
}
